class GeometryDiagram extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.diagram = null;
    /** @type {HTMLElement[]} */
    this.rows = [];
    /** @type {(HTMLElement | SVGElement)[]} */
    this.geoNodes = [];

    /** @type {string | null} */
    this.activeKey = null;
    /** @type {string | null} */
    this.lockedKey = null;

    /** @type {AbortController | null} */
    this._abort = null;
  }

  connectedCallback() {
    this.diagram = this.querySelector('[data-geo-diagram]');
    this.rows = Array.from(this.querySelectorAll('[data-geo-row]'));

    if (!this.diagram || this.rows.length === 0) return;

    const controller = new AbortController();
    this._abort = controller;

    for (const row of this.rows) {
      row.addEventListener('pointerenter', (e) => this.onEnter(e), { signal: controller.signal });
      row.addEventListener('pointerleave', (e) => this.onLeave(e), { signal: controller.signal });
      row.addEventListener('focusin', (e) => this.onFocus(e), { signal: controller.signal });
      row.addEventListener('focusout', (e) => this.onBlur(e), { signal: controller.signal });
      row.addEventListener('click', (e) => this.onClick(e), { signal: controller.signal });
    }

    // Support both attributes:
    // - data-geo="A" (our section convention)
    // - data-geometry-type="A" (as used in some exported geometry SVGs)
    this.geoNodes = /** @type {(HTMLElement | SVGElement)[]} */ (
      Array.from(this.diagram.querySelectorAll('[data-geo], [data-geometry-type]'))
    );
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  /** @param {HTMLElement | null | undefined} row */
  getKeyFromRow(row) {
    const key = row?.dataset?.geoKey;
    return typeof key === 'string' ? this.normalizeKey(key) : '';
  }

  /** @param {unknown} value */
  normalizeKey(value) {
    return (value == null ? '' : String(value)).trim().toUpperCase();
  }

  /** @param {HTMLElement | SVGElement} node */
  getKeyFromNode(node) {
    // Prefer dataset, but fall back to getAttribute for SVG compatibility.
    const fromDataset = node.dataset?.geo || node.dataset?.geometryType;
    if (fromDataset) return this.normalizeKey(fromDataset);

    const fromAttr = node.getAttribute('data-geo') || node.getAttribute('data-geometry-type');
    return this.normalizeKey(fromAttr);
  }

  prefersHover() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  isLocked() {
    return typeof this.lockedKey === 'string' && this.lockedKey.length > 0;
  }

  /** @param {Event} event */
  onEnter(event) {
    if (!this.prefersHover()) return;
    if (this.isLocked()) return;

    const row = /** @type {HTMLElement} */ (event.currentTarget);
    const key = this.getKeyFromRow(row);
    if (!key) return;

    this.setActive(key);
  }

  /** @param {Event} _event */
  onLeave(_event) {
    if (!this.prefersHover()) return;
    if (this.isLocked()) return;

    this.clearActive();
  }

  /** @param {Event} event */
  onFocus(event) {
    if (this.isLocked()) return;

    const row = /** @type {HTMLElement} */ (event.currentTarget);
    const key = this.getKeyFromRow(row);
    if (!key) return;

    this.setActive(key);
  }

  /** @param {Event} _event */
  onBlur(_event) {
    if (this.isLocked()) return;

    this.clearActive();
  }

  /** @param {Event} event */
  onClick(event) {
    // On touch devices (or anyone), allow click to lock/unlock the highlight.
    const row = /** @type {HTMLElement} */ (event.currentTarget);
    const key = this.getKeyFromRow(row);
    if (!key) return;

    if (this.lockedKey === key) {
      this.lockedKey = null;
      this.clearActive();
      return;
    }

    this.lockedKey = key;
    this.setActive(key);
  }

  /** @param {string} key */
  setActive(key) {
    const normalizedKey = this.normalizeKey(key);
    if (!normalizedKey) return;

    if (this.activeKey === normalizedKey) return;
    this.activeKey = normalizedKey;

    for (const row of this.rows) {
      row.classList.toggle('is-active', this.getKeyFromRow(row) === normalizedKey);
    }

    let matchedCount = 0;
    if (this.geoNodes) {
      for (const node of this.geoNodes) {
        const nodeKey = this.getKeyFromNode(/** @type {HTMLElement | SVGElement} */ (node));
        const isMatch = nodeKey === normalizedKey;
        node.classList.toggle('is-active', isMatch);
        if (isMatch) matchedCount += 1;
      }
    }

    // Only dim “other” overlay elements if at least one match exists.
    if (matchedCount > 0) {
      this.diagram?.classList.add('has-active');
    } else {
      this.diagram?.classList.remove('has-active');
    }
  }

  clearActive() {
    this.activeKey = null;

    for (const row of this.rows) {
      row.classList.remove('is-active');
    }

    if (this.geoNodes) {
      for (const node of this.geoNodes) {
        node.classList.remove('is-active');
      }
    }

    this.diagram?.classList.remove('has-active');
  }
}

if (!customElements.get('geometry-diagram')) {
  customElements.define('geometry-diagram', GeometryDiagram);
}
